import React, { useMemo, useState } from "react";
import { openExternalUrl } from "../lib/appMode";
import { apiUrl } from "../lib/apiBase";

const QUICK_GIFTS = [5, 10, 25, 50, 100];
const MIN_DONATION = 1;
const MAX_DONATION = 50000;

function normalizeAmount(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;

  const rounded = Math.round(parsed * 100) / 100;
  if (rounded < MIN_DONATION || rounded > MAX_DONATION) return null;

  return rounded;
}

export default function Donate() {
  const [amount, setAmount] = useState("");
  const [busyAmount, setBusyAmount] = useState(null);
  const [message, setMessage] = useState("");

  const customAmount = useMemo(() => normalizeAmount(amount), [amount]);

  const payCard = async (rawAmount) => {
    const donationAmount = normalizeAmount(rawAmount);

    if (donationAmount === null) {
      setMessage(`Enter an amount between $${MIN_DONATION} and $${MAX_DONATION.toLocaleString()}.`);
      return;
    }

    if (busyAmount !== null) return;

    setBusyAmount(donationAmount);
    setMessage("Opening secure Stripe Checkout…");

    try {
      const response = await fetch(apiUrl("/donations/create-checkout-session"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amountUsd: donationAmount }),
      });

      const text = await response.text();
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          data?.message || data?.error || `Checkout could not start (${response.status}).`
        );
      }

      if (!data?.url) {
        throw new Error("Stripe did not return a checkout link.");
      }

      await openExternalUrl(data.url);
    } catch (error) {
      console.error("[donate] checkout failed", error);
      setMessage(error?.message || "Donation checkout could not be opened. Please try again.");
      setBusyAmount(null);
    }
  };

  return (
    <div className="page-wrap">
      <div className="overlay" />

      <h1 className="text-2xl font-bold mb-4">Support Get Active Streaming</h1>

      <div className="card pad mb-6">
        <p>
          Your donation helps keep servers online, improves site reliability &amp; performance,
          and funds new features for the whole community. 🙏
        </p>
      </div>

      <div className="card pad mb-6">
        <h2 className="h2">Quick gifts</h2>
        <div className="quick-gifts">
          {QUICK_GIFTS.map((giftAmount) => {
            const isBusy = busyAmount === giftAmount;

            return (
              <div key={giftAmount} className="gift-btn">
                <button
                  type="button"
                  disabled={busyAmount !== null}
                  onClick={() => payCard(giftAmount)}
                >
                  {isBusy ? "Opening Stripe…" : `Quick Gift $${giftAmount}`}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card pad mb-6">
        <h2 className="h2">Custom amount</h2>
        <input
          type="number"
          className="input"
          aria-label="Custom donation amount in US dollars"
          inputMode="decimal"
          min={MIN_DONATION}
          max={MAX_DONATION}
          step="0.01"
          placeholder="USD"
          value={amount}
          disabled={busyAmount !== null}
          onChange={(event) => {
            setAmount(event.target.value);
            setMessage("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") payCard(customAmount);
          }}
        />
        <button
          type="button"
          className="btn-primary mt-2"
          disabled={busyAmount !== null || customAmount === null}
          onClick={() => payCard(customAmount)}
        >
          {busyAmount !== null && busyAmount === customAmount
            ? "Opening Stripe…"
            : "Donate with Card"}
        </button>

        {message ? (
          <p className="mt-2 small muted" role="status" aria-live="polite">
            {message}
          </p>
        ) : null}
      </div>

      <div className="card pad mb-6">
        <h2 className="h2">Where your gift goes</h2>
        <ul className="list-disc pl-6">
          <li>Hosting &amp; bandwidth</li>
          <li>Site performance &amp; stability</li>
          <li>New streamer features &amp; tools</li>
          <li>Community events &amp; improvements</li>
        </ul>
      </div>

      <div className="card pad">
        <h2 className="h2">Secure payments</h2>
        <p>
          Card details never touch our servers. Stripe handles payments with industry-standard
          security and encryption.
        </p>
        <p className="mt-2 small muted">PCI-DSS compliant • HTTPS end-to-end</p>
      </div>
    </div>
  );
}
